<template>
	<div class="module-form">
		<!-- Header -->
		<div class="module-form__header">
			<h3 class="module-form__title">
				{{ isEdit ? 'Редактирование модуля' : 'Создание модуля' }}
			</h3>
			<button
				class="module-form__close-btn"
				type="button"
				@click="handleCancel"
				title="Закрыть"
			>
				✕
			</button>
		</div>

		<!-- Form -->
		<form class="module-form__form" @submit.prevent="handleSubmit">
			<!-- Title -->
			<div class="module-form__field">
				<label class="module-form__label" for="module-title">
					Название модуля <span class="required">*</span>
				</label>
				<input
					id="module-title"
					v-model="formData.title"
					class="module-form__input"
					type="text"
					placeholder="Введите название модуля"
					required
				/>
			</div>

			<!-- Description -->
			<div class="module-form__field">
				<label class="module-form__label" for="module-description">
					Описание
				</label>
				<textarea
					id="module-description"
					v-model="formData.description"
					class="module-form__textarea"
					placeholder="Опишите содержание модуля"
					rows="4"
				></textarea>
			</div>

			<!-- Duration -->
			<div class="module-form__field">
				<label class="module-form__label" for="module-duration">
					Длительность (минуты)
				</label>
				<input
					id="module-duration"
					v-model.number="formData.duration_hours"
					class="module-form__input"
					type="number"
					min="0"
					step="0.01"
					placeholder="Примерная длительность модуля"
				/>
			</div>

			<!-- Cover Upload -->
			<div class="module-form__field">
				<label class="module-form__label">Обложка модуля</label>
				<div class="module-form__upload">
					<div v-if="coverPreview" class="module-form__preview">
						<img :src="coverPreview" alt="Превью обложки" />
						<button
							class="module-form__preview-remove"
							type="button"
							@click="removeCover"
							title="Удалить обложку"
						>
							✕
						</button>
					</div>
					<button
						v-else
						class="module-form__upload-btn"
						type="button"
						@click="selectCover"
					>
						<span class="module-form__upload-icon">📁</span>
						Выбрать обложку
					</button>
					<input
						ref="coverInput"
						type="file"
						accept="image/*"
						style="display: none"
						@change="handleCoverChange"
					/>
				</div>
			</div>

			<!-- Draft Checkbox -->
			<div class="module-form__field module-form__field--checkbox">
				<label class="module-form__checkbox-label">
					<input
						v-model="formData.is_draft"
						type="checkbox"
						class="module-form__checkbox"
					/>
					<span>Сохранить как черновик</span>
				</label>
			</div>

			<!-- Completion Policy -->
			<div class="module-form__field module-form__field--panel">
				<label class="module-form__label">Завершение модуля</label>
				<div class="module-form__panel">
					<label class="module-form__checkbox-label">
						<input
							v-model="completionEnabled"
							type="checkbox"
							class="module-form__checkbox"
						/>
						<span>Требовать финальный тест для завершения</span>
					</label>
					<div class="module-form__panel-row">
						<label class="module-form__label" for="final-test-select">
							Финальный тест
						</label>
						<select
							id="final-test-select"
							v-model="completionFinalTestId"
							class="module-form__input"
							:disabled="!completionEnabled || !testsAvailable"
						>
							<option value="">Выберите тест</option>
							<option v-for="test in moduleTests" :key="test.id" :value="String(test.id)">
								{{ test.title || `Тест ${test.id}` }}
							</option>
						</select>
						<p v-if="completionEnabled && !testsAvailable" class="module-form__hint">
							Сначала создайте тесты в этом модуле.
						</p>
					</div>
					<div class="module-form__panel-row">
						<label class="module-form__label" for="cap-percent">
							Кап прогресса до сдачи, %
						</label>
						<input
							id="cap-percent"
							v-model.number="completionCapPercent"
							class="module-form__input"
							type="number"
							min="0"
							max="100"
							step="1"
							:disabled="!completionEnabled"
						/>
					</div>
						<div class="module-form__panel-row module-form__panel-row--rules">
							<label class="module-form__label">Требования к завершению для доступа</label>
							<div class="module-form__rules">
								<label class="module-form__checkbox-label">
									<input
										v-model="finalTestRequireLessons"
										type="checkbox"
										class="module-form__checkbox"
										:disabled="!completionEnabled"
									/>
									<span>Уроки</span>
								</label>
								<label class="module-form__checkbox-label">
									<input
										v-model="finalTestRequirePractice"
										type="checkbox"
										class="module-form__checkbox"
										:disabled="!completionEnabled"
									/>
									<span>Практики</span>
								</label>
								<label class="module-form__checkbox-label">
									<input
										v-model="finalTestRequireHomework"
										type="checkbox"
										class="module-form__checkbox"
										:disabled="!completionEnabled"
									/>
									<span>Домашние задания</span>
								</label>
								<label class="module-form__checkbox-label">
									<input
										v-model="finalTestRequireTests"
										type="checkbox"
										class="module-form__checkbox"
										:disabled="!completionEnabled"
									/>
									<span>Тесты</span>
								</label>
							</div>
						</div>
						<div class="module-form__panel-row module-form__panel-row--time">
							<label class="module-form__label">Окно доступа</label>
							<label class="module-form__checkbox-label">
								<input
									v-model="finalTestWindowEnabled"
									type="checkbox"
									class="module-form__checkbox"
									:disabled="!completionEnabled"
								/>
								<span>Ограничить по времени</span>
							</label>
						<div class="module-form__time-window">
							<div class="module-form__time-field">
								<label class="module-form__label module-form__label--compact">Дата доступа</label>
								<DateTimePicker
									v-model="finalTestWindowDate"
									mode="date"
									placeholder="Выберите дату"
									:disabled="!completionEnabled || !finalTestWindowEnabled"
								/>
							</div>
							<div class="module-form__time-field">
								<label class="module-form__label module-form__label--compact">Время доступа</label>
								<DateTimePicker
									v-model="finalTestWindowTime"
									mode="time"
									placeholder="Выберите время"
									:disabled="!completionEnabled || !finalTestWindowEnabled"
								/>
							</div>
							<div class="module-form__time-field">
								<label class="module-form__label module-form__label--compact">
									Количество времени доступа, минут
								</label>
								<input
									v-model.number="finalTestWindowDurationMinutes"
									class="module-form__input"
									type="number"
									min="1"
									step="1"
									placeholder="120"
									:disabled="!completionEnabled || !finalTestWindowEnabled"
								/>
							</div>
						</div>
							<p class="module-form__hint">
								Студенты смогут начать тест только внутри указанного окна.
							</p>
						</div>
				</div>
			</div>

			<!-- Actions -->
			<div class="module-form__actions">
				<button
					class="module-form__btn module-form__btn--cancel"
					type="button"
					@click="handleCancel"
					:disabled="saving"
				>
					Отменить
				</button>
				<button
					class="module-form__btn module-form__btn--submit"
					type="submit"
					:disabled="saving"
				>
					{{ saving ? 'Сохранение...' : isEdit ? 'Обновить' : 'Создать' }}
				</button>
			</div>
		</form>
	</div>
</template>

<script>
import DateTimePicker from '@/components/ui/DateTimePicker.vue'

export default {
	name: 'ModuleForm',
	components: {
		DateTimePicker,
	},
	props: {
		module: {
			type: Object,
			default: null,
		},
		moduleTests: {
			type: Array,
			default: () => [],
		},
		saving: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			formData: {
				title: '',
				description: '',
				duration_hours: null,
				is_draft: true,
			},
			completionEnabled: false,
			completionFinalTestId: '',
			completionCapPercent: 95,
			finalTestRequireLessons: false,
			finalTestRequirePractice: false,
			finalTestRequireHomework: false,
			finalTestRequireTests: false,
			finalTestWindowEnabled: false,
			finalTestWindowDate: '',
			finalTestWindowTime: '',
			finalTestWindowDurationMinutes: 120,
			coverFile: null,
			coverPreview: null,
		}
	},
	computed: {
		isEdit() {
			return !!this.module
		},
		testsAvailable() {
			return Array.isArray(this.moduleTests) && this.moduleTests.length > 0
		},
	},
	mounted() {
		if (this.module) {
			this.formData = {
				title: this.module.title || '',
				description: this.module.description || '',
				duration_hours: this.module.duration_hours ?? this.module.duration_minutes ?? null,
				is_draft: this.module.is_draft || false,
			}
			this.coverPreview = this.module.cover_image || null

			const rawPolicy = this.module.completion_policy || this.module.completionPolicy
			let policy = rawPolicy
			if (typeof rawPolicy === 'string') {
				try {
					policy = JSON.parse(rawPolicy)
				} catch (error) {
					policy = null
				}
			}
			const finalTestId =
				policy?.final_test_id ?? policy?.finalTestId ?? null
			if (finalTestId) {
				this.completionEnabled = true
				this.completionFinalTestId = String(finalTestId)
			}
			if (policy?.cap_percent ?? policy?.capPercent) {
				const cap = Number(policy?.cap_percent ?? policy?.capPercent)
				if (Number.isFinite(cap)) {
					this.completionCapPercent = Math.min(Math.max(cap, 0), 100)
				}
			}
			const accessRules = policy?.access_rules ?? policy?.accessRules ?? null
			if (accessRules) {
				const requireAll =
					accessRules?.require_all_items ?? accessRules?.requireAllItems
				const requireItems =
					accessRules?.require_items ?? accessRules?.requireItems ?? null
				if (requireAll) {
					this.finalTestRequireLessons = true
					this.finalTestRequirePractice = true
					this.finalTestRequireHomework = true
					this.finalTestRequireTests = true
				} else if (Array.isArray(requireItems)) {
					const normalized = requireItems.map(item =>
						String(item || '').trim().toLowerCase()
					)
					this.finalTestRequireLessons = normalized.includes('lessons')
					this.finalTestRequirePractice = normalized.some(value =>
						['practice', 'practical', 'practicals'].includes(value)
					)
					this.finalTestRequireHomework = normalized.some(value =>
						['homework', 'homeworks'].includes(value)
					)
					this.finalTestRequireTests = normalized.some(value =>
						['test', 'tests'].includes(value)
					)
				} else if (requireItems && typeof requireItems === 'object') {
					this.finalTestRequireLessons = Boolean(requireItems.lessons)
					this.finalTestRequirePractice = Boolean(requireItems.practice)
					this.finalTestRequireHomework = Boolean(requireItems.homework)
					this.finalTestRequireTests = Boolean(requireItems.tests)
				}

				const timeWindow =
					accessRules?.time_window ?? accessRules?.timeWindow ?? null
				const startAt = timeWindow?.start_at ?? timeWindow?.startAt ?? ''
				const duration = Number(
					timeWindow?.duration_minutes ?? timeWindow?.durationMinutes ?? 0
				)
				if (startAt) {
					const parts = this.parseWindowStartAt(startAt)
					this.finalTestWindowDate = parts.date
					this.finalTestWindowTime = parts.time
				}
				if (Number.isFinite(duration) && duration > 0) {
					this.finalTestWindowDurationMinutes = duration
				}
				this.finalTestWindowEnabled = !!(
					(this.finalTestWindowDate && this.finalTestWindowTime) ||
					(Number.isFinite(duration) && duration > 0)
				)
			}
		}
	},
	methods: {
		parseWindowStartAt(value) {
			if (!value) return { date: '', time: '' }
			const raw = String(value)
			const dateMatch = raw.match(/\d{4}-\d{2}-\d{2}/)
			const timeMatch = raw.match(/\d{2}:\d{2}/)
			return {
				date: dateMatch ? dateMatch[0] : '',
				time: timeMatch ? timeMatch[0] : '',
			}
		},

		composeWindowStartAt() {
			if (!this.finalTestWindowDate || !this.finalTestWindowTime) return ''
			return `${this.finalTestWindowDate}T${this.finalTestWindowTime}`
		},

		selectCover() {
			this.$refs.coverInput.click()
		},

		handleCoverChange(event) {
			const file = event.target.files[0]
			if (!file) return

			// Проверка размера (макс 5MB)
			if (file.size > 5 * 1024 * 1024) {
				alert('Размер файла не должен превышать 5MB')
				return
			}

			// Проверка типа
			if (!file.type.startsWith('image/')) {
				alert('Пожалуйста, выберите изображение')
				return
			}

			this.coverFile = file

			// Создаем превью
			const reader = new FileReader()
			reader.onload = e => {
				this.coverPreview = e.target.result
			}
			reader.readAsDataURL(file)
		},

		removeCover() {
			this.coverFile = null
			this.coverPreview = null
			if (this.$refs.coverInput) {
				this.$refs.coverInput.value = ''
			}
		},

		handleSubmit() {
			if (!this.formData.title.trim()) {
				alert('Пожалуйста, введите название модуля')
				return
			}

			if (this.completionEnabled && !this.completionFinalTestId) {
				alert('Выберите финальный тест для завершения модуля.')
				return
			}

			const accessRules = {}
			if (this.completionEnabled) {
				const requireItems = {
					lessons: this.finalTestRequireLessons,
					practice: this.finalTestRequirePractice,
					homework: this.finalTestRequireHomework,
					tests: this.finalTestRequireTests,
				}
				const hasRequireItems = Object.values(requireItems).some(Boolean)
				const windowDuration = Number(this.finalTestWindowDurationMinutes) || 0
				const windowStartAt = this.composeWindowStartAt()
				const hasWindow =
					this.finalTestWindowEnabled && windowStartAt && windowDuration > 0
				if (this.finalTestWindowEnabled && !hasWindow) {
					alert('Заполните дату и длительность окна доступа.')
					return
				}
				if (hasRequireItems) {
					accessRules.require_items = requireItems
				}
				if (hasWindow) {
					accessRules.time_window = {
						start_at: windowStartAt,
						duration_minutes: windowDuration,
					}
				}
			}

			const data = {
				...this.formData,
				coverFile: this.coverFile,
				completion_policy: this.completionEnabled
					? {
							final_test_id: Number(this.completionFinalTestId),
							cap_percent: Math.min(
								Math.max(Number(this.completionCapPercent) || 0, 0),
								100
							),
							access_rules: Object.keys(accessRules).length
								? accessRules
								: undefined,
					  }
					: null,
			}

			this.$emit('submit', data)
		},

		handleCancel() {
			if (
				confirm(
					'Вы уверены, что хотите отменить? Все несохраненные данные будут потеряны.'
				)
			) {
				this.$emit('cancel')
			}
		},
	},
}
</script>

<style scoped>
.module-form {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

/* Header */
.module-form__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 2px solid #e8e8e8;
}

.module-form__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 22px;
	font-weight: 600;
	margin: 0;
}

.module-form__close-btn {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: transparent;
	border: none;
	border-radius: 50%;
	color: #636363;
	font-size: 24px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.module-form__close-btn:hover {
	background-color: #f5f5f5;
	color: #dc3545;
}

/* Form */
.module-form__form {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.module-form__field {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.module-form__field--panel {
	gap: 12px;
}

.module-form__label {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
}

.module-form__panel {
	border: 2px solid #e8e8e8;
	border-radius: 10px;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	background-color: #fafafa;
}

.module-form__panel-row {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.module-form__rules {
	display: flex;
	flex-wrap: wrap;
	gap: 12px 16px;
}

.module-form__time-window {
	display: grid;
	grid-template-columns: minmax(180px, 1fr) minmax(140px, 180px) 200px;
	gap: 12px;
	align-items: start;
}

.module-form__time-field {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.module-form__label--compact {
	margin-bottom: 0;
	font-size: 11px;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: rgba(37, 38, 65, 0.7);
}

.module-form__hint {
	margin: 0;
	font-size: 12px;
	color: #8a8a8a;
}

.required {
	color: #dc3545;
}

.module-form__input,
.module-form__textarea {
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	color: #252641;
	transition: all 0.2s ease;
}

.module-form__input:focus,
.module-form__textarea:focus {
	outline: none;
	border-color: #0095ec;
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.1);
}

.module-form__textarea {
	resize: vertical;
	min-height: 100px;
}

/* Upload */
.module-form__upload {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.module-form__preview {
	position: relative;
	width: 200px;
	height: 120px;
	border-radius: 8px;
	overflow: hidden;
}

.module-form__preview img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.module-form__preview-remove {
	position: absolute;
	top: 8px;
	right: 8px;
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: rgba(220, 53, 69, 0.9);
	border: none;
	border-radius: 50%;
	color: #ffffff;
	font-size: 18px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.module-form__preview-remove:hover {
	background-color: #dc3545;
	transform: scale(1.1);
}

.module-form__upload-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 12px 20px;
	border: 2px dashed #e8e8e8;
	border-radius: 8px;
	background-color: #f8f8f8;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.module-form__upload-btn:hover {
	border-color: #0095ec;
	background-color: #f0f8ff;
	color: #0095ec;
}

.module-form__upload-icon {
	font-size: 20px;
}

/* Checkbox */
.module-form__field--checkbox {
	flex-direction: row;
	align-items: center;
}

.module-form__checkbox-label {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
}

.module-form__checkbox {
	width: 18px;
	height: 18px;
	cursor: pointer;
}

/* Actions */
.module-form__actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
	padding-top: 20px;
	border-top: 2px solid #e8e8e8;
}

.module-form__btn {
	padding: 12px 24px;
	border: none;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.module-form__btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.module-form__btn--cancel {
	background-color: #f5f5f5;
	color: #636363;
}

.module-form__btn--cancel:hover:not(:disabled) {
	background-color: #e8e8e8;
}

.module-form__btn--submit {
	background-color: #0095ec;
	color: #ffffff;
}

.module-form__btn--submit:hover:not(:disabled) {
	background-color: #0077be;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}
</style>

